/**
 * Formulaire de connexion créateur — /connexion
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { useAuth } from '@/lib/auth';
import { useT, useLang } from '@/lib/i18n';
import { setPageMeta } from '@/lib/seo';
import { toast } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';

export default function Login() {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '/studio';
  const { user, loading, login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPageMeta({
      title: t('seo.login_title'),
      description: t('seo.login_description'),
      path: '/connexion',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  useEffect(() => {
    if (!loading && user) navigate(nextPath, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError(t('auth.errors.fill_both'));
      return;
    }
    setSubmitting(true);
    const res = await login({ email: email.trim().toLowerCase(), password });
    setSubmitting(false);
    if (res.ok) {
      toast.success(t('auth.login_success'), { duration: 3500 });
      navigate(nextPath, { replace: true });
    } else {
      const msg = t(`errors.${res.code || 'auth_generic'}`);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    }
  };

  return (
    <div data-testid="page-login">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-24">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute left-1/2 -top-24 h-[420px] w-[420px] -translate-x-1/2 rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(14,165,233,0.28), transparent 70%)' }}
            />
          </div>

          <div className="om-container max-w-md">
            <div className="rounded-[28px] border border-[var(--om-border)] bg-[var(--om-bg-pure)] p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-10">
              <span className="om-eyebrow">{t('auth.login_eyebrow')}</span>
              <h1 className="om-h1 mt-4 text-[30px] sm:text-[36px] text-[var(--om-ink)]">
                {t('auth.login_title')}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-[var(--om-muted)]">
                {t('auth.login_subtitle')}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                    {t('auth.email_label')}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3.5 py-2.5 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] focus:border-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
                    placeholder="vous@openmeta.tech"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                    {t('auth.password_label')}
                  </label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-xl border border-[var(--om-border)] bg-[var(--om-bg)] px-3.5 py-2.5 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] focus:border-[var(--om-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--om-ink)]/10"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>

                {error && <div className="text-[12px] text-rose-500" data-testid="login-error">{error}</div>}

                <button
                  type="submit"
                  disabled={submitting}
                  className="om-btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="login-submit-btn"
                >
                  {submitting && <Loader2 size={14} strokeWidth={2.5} className="animate-spin" />}
                  {submitting ? t('auth.submitting') : t('auth.login_cta')}
                  {!submitting && <ArrowRight size={14} strokeWidth={2.4} />}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-[var(--om-muted)]">
                {t('auth.no_account')}{' '}
                <Link
                  to={`/inscription${nextPath !== '/studio' ? `?next=${encodeURIComponent(nextPath)}` : ''}`}
                  className="font-semibold text-[var(--om-ink)] underline underline-offset-4"
                  data-testid="login-goto-register"
                >
                  {t('auth.register_cta')}
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
